// import React in our code
import React, {useState, useEffect, useContext, useRef} from 'react';
import {Animated, Easing, Platform} from 'react-native';
import {CommonActions} from '@react-navigation/native';

// import all the components we are going to use
import styles from './styles';
import {Images} from '../../utils/Images';
import {
  getData,
  getUserProfileData,
  storeData,
} from '../../utils/GeneralStorage';
import {KEYS, USER_AUTH_STATUS} from '../../utils/Constants';
import {useTheme} from '../../theme';
import {tID} from '../../utils/TestIdHelper';
import {useNavigate} from '../../utils/mockedWebLibs/react-router-dom';
import {setupAnalytics} from '../../utils/Analytics';
import {eligibilityCheckContext} from '../../contexts/EligibilityCheckContext';
import moment from 'moment';
import {compareTokenVersions} from '../../utils/HelperMethods';

const SplashScreen = ({navigation}) => {
  const navigate = useNavigate();
  const {colors} = useTheme();
  const endValue = -50;
  const [visible, setVisible] = useState(false);
  // Animated values MUST survive re-renders: useTheme re-renders this screen
  // mid-boot (persisted mode loads async), and recreating these orphaned the
  // running animation — the view froze on the new values and the completion
  // callback that routes onward never fired (app stuck on splash).
  const startValue = useRef(new Animated.Value(50)).current;
  const backgroundChange = useRef(new Animated.Value(0)).current;
  const zoomWidth = useRef(new Animated.Value(1000)).current;
  const zoomHeight = useRef(new Animated.Value(1000)).current;
  // JS-layer splash colors only — the native splash (LaunchScreen storyboard
  // / Android launch theme) is a fixed light asset; known gap in
  // docs/DARK-MODE.md.
  const backgroundColor = backgroundChange.interpolate({
    inputRange: [0, 255],
    outputRange: [colors.backgroundMuted, colors.accentStrong],
  });

  const ecc = useContext(eligibilityCheckContext);

  const imageZoomout = () => {
    Animated.parallel([
      Animated.timing(
        zoomWidth, // The animated value to drive
        {
          toValue: 66,
          useNativeDriver: false,
        },
      ),
      Animated.timing(
        zoomHeight, // The animated value to drive
        {
          toValue: 76,
          useNativeDriver: false,
        },
      ),
    ]).start(({finished}) => {
      if (finished) {
        setVisible(true);
      }
    });
  };

  useEffect(() => {
    imageZoomout();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot zoom animation must start exactly once; imageZoomout is re-created each render
  }, [ecc]);

  const imageMovement = () => {
    Animated.sequence([
      Animated.timing(startValue, {
        toValue: endValue,
        duration: 500,
        easing: Easing.linear, // the style of animation
        useNativeDriver: true,
      }),
      Animated.timing(backgroundChange, {
        toValue: 255,
        duration: 300,
        easing: Easing.linear, // the style of animation
        useNativeDriver: false,
      }),
      Animated.timing(backgroundChange, {
        toValue: 0,
        duration: 300,
        easing: Easing.linear, // the style of animation
        useNativeDriver: false,
      }),
    ]).start(async res => {
      if (res.finished) {
        if (Platform.OS !== 'web') await setupAnalytics();
        const isLogin = await getData(KEYS.isLogin);
        const token = await getData(KEYS.userToken);
        let routeName = getRouteName('login');
        if (isLogin === USER_AUTH_STATUS.login && token) {
          const isTokenVersionUpdated = await compareTokenVersions();
          if (isTokenVersionUpdated) {
            // Once the app has been updated, older users will go through this check.
            // Every time a user opens an app, they go through this check.
            routeName = await redirectByEligibilityCheck();
          }
        }

        if (Platform.OS === 'web') {
          navigate(routeName);
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                routeName === 'OnBoardingExperience'
                  ? {
                      name: routeName,
                    }
                  : {
                      name: 'HomeTab',
                      params: {
                        screen: routeName,
                      },
                    },
              ],
            }),
          );
        }
      }
    });
  };

  useEffect(() => {
    if (visible) {
      imageMovement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- splash animation + auth routing must run only on the visible transition; imageMovement is re-created each render
  }, [visible]);

  async function redirectByEligibilityCheck() {
    let routeName;
    const profile = await getUserProfileData();
    // No readable profile (cleared storage, unreadable Keychain, partial
    // state) → route to login instead of crashing in checkUserEligibility
    // and wedging the app on the splash screen.
    if (!profile) {
      return getRouteName('login');
    }
    //checking user employer needs eligibility check or not
    if (profile?.employer?.realtimeFlowId === 0) {
      //redirecting to normal flow if user employer doesn't need eligibility check
      await storeData(KEYS.userEligible, 'true');
      ecc.setIsEligible(true);
      routeName = getRouteName('home');
    } else {
      //checking Eligibilty based on employer realtimeFlowId
      const lastEligibilityCheckTime = await getData(
        KEYS.lastEligibilityCheckTime,
      );

      if (lastEligibilityCheckTime) {
        const diffInHours = moment().diff(
          moment(lastEligibilityCheckTime),
          'hours',
        );

        if (diffInHours > 72) {
          checkUserEligibility(profile);
          routeName = getRouteName('shopping');
        } else {
          const isEligible = await getData(KEYS.userEligible);
          ecc.setIsEligible(isEligible === 'true');
          routeName = getRouteName('home');
        }
      } else {
        checkUserEligibility(profile);
        routeName = getRouteName('shopping');
      }
    }

    return routeName;
  }

  function getRouteName(screen) {
    let routeName = Platform.OS === 'web' ? '/login' : 'OnBoardingExperience';
    switch (screen) {
      case 'home':
        routeName = Platform.OS === 'web' ? '/home' : 'HomePage';
        break;
      case 'shopping':
        routeName = Platform.OS === 'web' ? '/store' : 'Shopping';
        break;
      default:
        break;
    }

    return routeName;
  }

  //checking Eligibilty based on employer realtimeFlowId
  function checkUserEligibility(profile) {
    ecc.checkEligibilty(
      Platform.OS === 'web' ? navigate : navigation,
      profile.firstname + ' ' + profile.lastname,
      profile.email,
      profile.dateOfBirth,
      profile.subscriberId,
      profile?.employer?.realtimeFlowId,
    );
  }

  return (
    <Animated.View
      testID={tID('Splash-AnimatedView-Container')}
      style={[styles.container, {backgroundColor: backgroundColor}]}>
      {visible && (
        <Animated.Image
          testID={tID('Splash-AnimatedView-TitleImage')}
          source={Images.groupname}
          style={[
            styles.titleImage,
            {
              transform: [
                {
                  translateX: startValue,
                },
              ],
            },
          ]}
        />
      )}
      {!visible && (
        <Animated.Image
          testID={tID('Splash-AnimatedView-Logo')}
          source={Images.logo}
          style={{
            width: zoomWidth,
            height: zoomHeight,
          }}
        />
      )}
    </Animated.View>
  );
};

export default SplashScreen;
